import { useId } from 'react'
import type { ReactElement } from 'react'
import { Button } from './Button.tsx'
import { Select } from './Select.tsx'

/**
 * Page size, prev/next, and "N–M of T".
 *
 * The count is the point. A bare pair of arrows tells an operator nothing
 * about whether the thing they are looking for is three rows down or
 * three thousand, and "page 4 of 160" is a worse answer to that than
 * "76–100 of 3,994".
 *
 * Stateless: the page and the page size are the caller's, because on
 * every screen here they also belong in the URL.
 */

/** 25 first: it is roughly a screen of 32px rows, which is the size that
 * makes the dense table worth having. */
export const DEFAULT_PAGE_SIZES: readonly number[] = [25, 50, 100, 250]

export interface PaginationProps {
  /** 1-based, matching what the label says. */
  page: number
  pageSize: number
  /**
   * Rows matching the filter, ignoring the page — the T in "N–M of T".
   *
   * Omit it only when the list genuinely has no total to report. The vault
   * approvals queue is the case: its API answers a page plus a cursor and
   * never counts the rest, because counting every request a root has ever
   * raised is the query that made that route the slowest in the product.
   * The label then reports the position alone ("Page 2"), which is a
   * smaller answer than "76–100 of 3,994" but an honest one — inventing a
   * total from the rows fetched so far would read "1–25 of 25" on page one
   * of forty, next to an enabled Next button.
   */
  totalCount?: number
  pageSizeOptions?: readonly number[]
  /**
   * Whether a further page exists, for cursor-paged lists where that is
   * not arithmetic.
   *
   * Omit it and Next is enabled while `page < ceil(total / pageSize)`,
   * which is right for an offset list — and never, when `totalCount` is
   * omitted too, since there is then no arithmetic to fall back on. A
   * cursor list's authority is the
   * cursor the server returned, not the count — /sessions reports a
   * `total` for the whole filter and a `nextCursor` that is non-null only
   * when a row actually follows, and the two disagree whenever rows are
   * created or finish mid-walk. Enabling Next off the count there offers
   * a page the caller has no cursor to ask for.
   */
  hasNextPage?: boolean
  onPageChange: (page: number) => void
  /** Changing the size should return to page 1 — the caller does that,
   * because it may also need to clear a cursor or a selection. */
  onPageSizeChange: (pageSize: number) => void
}

/** "76–100 of 3,994", or "0 of 0" when there is nothing. An en dash, not
 * a hyphen: this is a range.
 *
 * With no total — a cursor list that does not count — there is no range to
 * report, only a position. "Page 4" is the whole honest answer; see
 * `PaginationProps.totalCount`. */
export function rangeLabel(
  page: number,
  pageSize: number,
  totalCount?: number,
): string {
  if (totalCount === undefined) return `Page ${page.toLocaleString()}`
  if (totalCount === 0) return '0 of 0'
  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, totalCount)
  // The page position and the total are read at different moments on a
  // cursor list: /sessions re-runs its loader every 5s, and under the
  // default `liveOnly` filter sessions finish out of the count while the
  // operator sits on a cursor already walked past the new total. `first`
  // then overshoots `last` and the naive range renders backwards —
  // "51–40 of 40", which reads as a rendering bug rather than as a
  // number that moved. Report the total alone instead of an impossible
  // range; `hasNextPage` is what the arrows go by either way.
  if (first > last) return `${totalCount.toLocaleString()} total`
  return `${first.toLocaleString()}–${last.toLocaleString()} of ${totalCount.toLocaleString()}`
}

/** Clamped at 1 so an empty result still reports "page 1 of 1" rather
 * than "page 1 of 0", which reads as a bug. */
export function pageCount(pageSize: number, totalCount: number): number {
  return Math.max(1, Math.ceil(totalCount / pageSize))
}

export function Pagination({
  page,
  pageSize,
  totalCount,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  hasNextPage,
  onPageChange,
  onPageSizeChange,
}: PaginationProps): ReactElement {
  // No total means no arithmetic: such a caller must supply
  // `hasNextPage`, and `false` is the safe read of a Next nobody vouched
  // for — offering a page the caller has no cursor to ask for is worse
  // than hiding one it does.
  const canGoNext =
    hasNextPage ??
    (totalCount === undefined ? false : page < pageCount(pageSize, totalCount))
  // Two tables on one page means two of these; a literal id would give
  // them the same one and point both labels at the first select.
  const pageSizeId = useId()

  return (
    <nav
      // Named, because a page with two tables has two of these and
      // "navigation" twice is not a choice.
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-between gap-2"
    >
      <div className="flex items-center gap-2">
        <label className="text-xs text-muted" htmlFor={pageSizeId}>
          Rows
        </label>
        <Select
          id={pageSizeId}
          value={pageSize}
          onChange={(event) => {
            onPageSizeChange(Number(event.target.value))
          }}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
      </div>
      <div className="flex items-center gap-2">
        {/* A live region: paging is the one place where the content
            changes under a control that stays put, so a screen reader
            user gets no other signal that anything happened. */}
        <span role="status" aria-live="polite" className="text-xs text-faint">
          {rangeLabel(page, pageSize, totalCount)}
        </span>
        <Button
          size="sm"
          onClick={() => {
            onPageChange(page - 1)
          }}
          isDisabled={page <= 1}
        >
          {/* No `aria-label` on either button. An accessible name that
              differs from the visible text breaks voice control (WCAG
              2.5.3 "Label in Name"), and inside a nav already named
              "Pagination" the visible word is unambiguous. */}
          Prev
        </Button>
        <Button
          size="sm"
          onClick={() => {
            onPageChange(page + 1)
          }}
          isDisabled={!canGoNext}
        >
          Next
        </Button>
      </div>
    </nav>
  )
}
